import { Navigate, useLocation, Outlet, useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import {useAuth} from './context/AuthContext';
import { useStoreMode } from './context/StoreContext.js';
import { useLanguage } from './context/LanguageContext.js';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import ErrorState from './components/ErrorState/index.js';
import {
    adminNeedsComplianceAcceptance,
    COMPLIANCE_ACCEPTANCE_REQUIRED,
} from './Utils/adminCompliance.js';
import { routeAllowedInStoreMode } from './constants/storeModes.js';
import {
    isBillingRestrictedPathAllowed,
    useBillingStatus,
} from './Utils/useBillingStatus.js';

const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading, user, passwordChangeRequired, authCheckFailed, checkAuth } = useAuth();
    const { needsStoreSelection, storeMode, ready: storeReady, stores } = useStoreMode();
    const { ready: billingReady, inactive: billingInactive } = useBillingStatus();
    const { t } = useLanguage();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    if (loading || !storeReady) {
        return <LoadingSpinner message={t('auth.authenticating')} />;
    }

    // A rate-limited or unreachable API is not an expired session. Bouncing to /login here
    // just fires another request at the same exhausted budget and looks like a broken app.
    if (!isAuthenticated && authCheckFailed) {
        return (
            <ErrorState
                title={t('auth.unavailable.title')}
                message={t('auth.unavailable.message')}
                actionLabel={t('auth.unavailable.retry')}
                onAction={checkAuth}
            />
        );
    }

    if (!isAuthenticated) {
        // Redirect to login, preserving target location
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    const passwordGateAllows = location.pathname === '/password-required';

    if (passwordChangeRequired && !passwordGateAllows) {
        return <Navigate to="/password-required" replace />;
    }

    // Wait for billing before allowing the rest of the app — expired orgs must stay gated.
    if (!billingReady && !passwordGateAllows) {
        return <LoadingSpinner message={t('auth.authenticating')} />;
    }

    const onComplianceTab =
        location.pathname === '/account-setting' && searchParams.get('tab') === 'compliance';
    const onBillingTab =
        location.pathname === '/account-setting' && searchParams.get('tab') === 'billing';
    const onAccountSetting = location.pathname === '/account-setting';
    const onSelectPlan =
        location.pathname === '/select-plan' ||
        location.pathname.startsWith('/select-plan/');

    const onSelectStore = location.pathname === '/select-store';
    const needsFirstStoreSetup = stores.length === 0;

    // Expired trial / canceled / past_due: only account settings, store pick, and plan/checkout.
    if (
        billingInactive &&
        !passwordGateAllows &&
        !isBillingRestrictedPathAllowed(location.pathname)
    ) {
        const target =
            needsStoreSelection && stores.length > 1
                ? '/select-store'
                : '/account-setting?tab=billing';
        return <Navigate to={target} replace />;
    }

    // New merchants must create their first store before other gates (compliance, home, etc.)
    // Billing + plan selection (including card/crypto checkout pages) stay reachable before
    // the first store exists so they can subscribe during onboarding / after trial ends.
    // Do not force ?create=1 — SelectStore opens create only when billing allows it.
    if (
        needsStoreSelection &&
        !onSelectStore &&
        !(needsFirstStoreSetup && (onBillingTab || onAccountSetting || onSelectPlan))
    ) {
        return <Navigate to="/select-store" replace />;
    }

    if (
        !passwordChangeRequired &&
        !needsFirstStoreSetup &&
        !onSelectStore &&
        COMPLIANCE_ACCEPTANCE_REQUIRED &&
        adminNeedsComplianceAcceptance(user) &&
        !onComplianceTab
    ) {
        return <Navigate to="/account-setting?tab=compliance" replace />;
    }

    if (
        storeMode &&
        !onSelectStore &&
        !passwordGateAllows &&
        !routeAllowedInStoreMode(location.pathname, storeMode)
    ) {
        return <Navigate to="/home" replace />;
    }

    return children ? children : <Outlet />;
};

ProtectedRoute.propTypes = {
    children: PropTypes.node,
};

export default ProtectedRoute;
